"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronUp, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import type { PlaygroundEngineId } from "../config/backend-language-contract.generated";
import type { PlaygroundLanguage } from "../config/languages";
import type { LanguagePlaygroundContent } from "../content/types";
import type { CodeExample } from "../content/types";
import type { PublicPlaygroundPromo, PublicSiteNotice } from "@/lib/platform-public-settings";
import { useLanguageGuideVisibility } from "../lib/language-guide-storage";
import { PlaygroundClient } from "./PlaygroundClient";
import { CodeExampleCards } from "./CodeExampleCards";
import { LanguageInfoSection } from "./LanguageInfoSection";

type Props = {
  locale: string;
  /** Which engine the API is serving, read server-side (`EX-PUB04B`). */
  engine: PlaygroundEngineId;
  language: PlaygroundLanguage;
  initialSnippetId: string | null;
  content: LanguagePlaygroundContent;
  /** `AD-SN01`: forwarded straight to `PlaygroundClient`. */
  siteNotice?: PublicSiteNotice;
  /** `GR-PGP01`: forwarded straight to `PlaygroundClient`. */
  playgroundPromo?: PublicPlaygroundPromo;
};

export function PlaygroundLanguageSeoShell({
  locale,
  engine,
  language,
  initialSnippetId,
  content,
  siteNotice,
  playgroundPromo,
}: Props) {
  const t = useTranslations("playground");
  const { hidden: guideHidden, setHidden: setGuideHidden } = useLanguageGuideVisibility();
  const [loadNonce, setLoadNonce] = useState(0);
  const [examplePayload, setExamplePayload] = useState<{
    sourceCode: string;
    stdin?: string;
  } | null>(null);

  /** IDE pane height as % of viewport; drag splitter to give more room to docs below. */
  const [ideVh, setIdeVh] = useState(56);
  const dragActiveRef = useRef(false);
  const startPointerYRef = useRef(0);
  const startIdeVhRef = useRef(56);

  const clampIdeVh = useCallback((vh: number) => {
    const ih = Math.max(window.innerHeight, 1);
    const minVh = Math.max(22, (280 / ih) * 100);
    const maxVh = 82;
    return Math.min(maxVh, Math.max(minVh, vh));
  }, []);

  const handleSplitterPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragActiveRef.current = true;
      startPointerYRef.current = e.clientY;
      startIdeVhRef.current = ideVh;
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [ideVh],
  );

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragActiveRef.current) return;
      const ih = window.innerHeight;
      const deltaVh = ((e.clientY - startPointerYRef.current) / ih) * 100;
      setIdeVh(clampIdeVh(startIdeVhRef.current + deltaVh));
    };
    const onUp = () => {
      dragActiveRef.current = false;
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };
  }, [clampIdeVh]);

  const handleSplitterKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      let delta = 0;
      if (e.key === "ArrowUp") delta = -3;
      else if (e.key === "ArrowDown") delta = 3;
      else return;
      e.preventDefault();
      setIdeVh((vh) => clampIdeVh(vh + delta));
    },
    [clampIdeVh],
  );

  const handleTryExample = useCallback(
    (ex: CodeExample) => {
      setExamplePayload({ sourceCode: ex.code, stdin: ex.stdin });
      setLoadNonce((n) => n + 1);
      toast(t("exampleLoaded"), { id: "playground-example-loaded" });
    },
    [t],
  );

  /** Stable reference unless nonce/payload change — avoids PlaygroundClient "Try this" effect loops. */
  const loadExampleRequest = useMemo(() => {
    if (examplePayload == null) return null;
    return {
      nonce: loadNonce,
      sourceCode: examplePayload.sourceCode,
      stdin: examplePayload.stdin,
    };
  }, [loadNonce, examplePayload]);

  const footerLine = t("footerLine", { language: language.name });

  return (
    <div className="flex h-[100dvh] max-h-[100dvh] min-h-0 flex-col overflow-hidden">
      <div
        className={
          guideHidden
            ? "flex min-h-0 flex-1 flex-col overflow-hidden"
            : "flex min-h-0 shrink-0 flex-col overflow-hidden"
        }
        style={
          guideHidden
            ? undefined
            : {
                height: `${ideVh}vh`,
                minHeight: 300,
              }
        }
      >
        <PlaygroundClient
          locale={locale}
          engine={engine}
          initialLanguageId={language.seoSlug}
          initialSnippetId={initialSnippetId}
          suppressDocumentHeading
          loadExampleRequest={loadExampleRequest}
          omitFooter
          hasLanguageGuide
          siteNotice={siteNotice}
          playgroundPromo={playgroundPromo}
        />
      </div>

      {guideHidden ? (
        <div className="flex shrink-0 items-center justify-center gap-3 border-t border-white/10 bg-[#0B0B0C] px-3 py-1">
          <footer className="py-1 text-center text-[11px] text-white/55" role="contentinfo">
            {footerLine}
          </footer>
          <button
            type="button"
            onClick={() => setGuideHidden(false)}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[11px] font-medium text-white/70 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45"
          >
            <ChevronUp className="h-3 w-3" aria-hidden />
            {t("guideShow")}
          </button>
        </div>
      ) : (
        <>
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-valuemin={26}
            aria-valuemax={82}
            aria-valuenow={Math.round(ideVh)}
            aria-label={t("seoIdeSplitterLabel")}
            tabIndex={0}
            onPointerDown={handleSplitterPointerDown}
            onKeyDown={handleSplitterKeyDown}
            className="group relative flex shrink-0 cursor-row-resize touch-none flex-col items-center justify-center border-t border-white/10 bg-[#0B0B0C] py-1 select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45"
          >
            <div
              className="mb-0.5 h-1 w-14 rounded-full bg-white/25 transition group-hover:bg-white/40 group-active:bg-[#E94D1D]/70"
              aria-hidden
            />
            <footer
              className="pointer-events-none px-3 py-1.5 text-center text-[11px] text-white/55"
              role="contentinfo"
            >
              {footerLine}
            </footer>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto border-t border-white/10 bg-[#0B0B0C] px-4 py-10 text-white sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl">
              <div className="mb-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setGuideHidden(true)}
                  title={t("guideHide")}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs font-medium text-white/60 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                  {t("guideHide")}
                </button>
              </div>
              <LanguageInfoSection locale={locale} language={language} content={content} />
              <CodeExampleCards examples={content.examples} onTry={handleTryExample} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
